import React, { useCallback, useEffect } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextareaAutosize,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { createAxiosInstance } from 'src/utils/axios';
import PurchaseOrderNoteService from 'src/services/PurchaseOrderNoteService';
import { purchaseOrderNoteTypes } from 'src/components/PurchaseOrderDetails/constants/purchaseOrderNoteTypes';

const validationSchema = yup.object({
  noteType: yup.string().required('Note Type must be selected'),
  noteText: yup.string('Enter the Note Text').required('Note Text is Required'),
});

// eslint-disable-next-line no-unused-vars
const useStyles = makeStyles((_theme) => ({
  dialogActions: {
    marginRight: 15,
  },
}));

const purchaseOrderNoteService = new PurchaseOrderNoteService(
  createAxiosInstance(process.env.REACT_APP_CASH_QORE_API)
);

function CreatePurchaseOrderNoteModal({ isDialogOpen, setIsDialogOpen, onSuccess, poNumber, dispatchId }) {
  const { email } = useSelector((state) => state.account.user);
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const onSubmit = useCallback(async (values) => {
    try {
      await purchaseOrderNoteService.createPurchaseOrderNote(poNumber, dispatchId, values.noteType, values.noteText, email);
      setIsDialogOpen(false);
      formik.resetForm();
      enqueueSnackbar('Note created successfully', { variant: 'success' });

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      enqueueSnackbar(`Error creating note. ${error.message}`, { variant: 'error' });
    }
  }, [poNumber, dispatchId]);

  const formik = useFormik({
    initialValues: {
      noteType: '1',
      noteText: '',
    },
    validationSchema: validationSchema,
    onSubmit,
  });

  useEffect(() => {
    if (isDialogOpen) {
      formik.validateForm();
    }
  }, [isDialogOpen]);

  return (
    <Dialog
      open={isDialogOpen}
      id="create-purchase-order-note-modal"
      data-test="create-purchase-order-note-modal"
      aria-labelledby="create-purchase-order-note-modal-title"
      aria-describedby="create-purchase-order-note-modal-content"
      fullWidth={true}
      maxWidth="md"
    >
      <DialogTitle
        id="selected-records-title"
        data-test="selected-records-title"
      >
      <Typography>Add a Note</Typography>
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent
          id="selected-records-content"
          data-test="selected-records-content"
        >
          <Grid container spacing={2} fullWidth>
            <Grid item xs={12}>
              <FormControl>
                <FormLabel id="demo-radio-buttons-group-label">Type</FormLabel>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  value={formik.values.noteType}
                  name="noteType"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  row
                >
                  {Object.values(purchaseOrderNoteTypes).map(value => {
                    return <FormControlLabel key={`${value.label}-${value.value}`} value={value.value} control={<Radio />} label={value.label} />;
                  })}
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextareaAutosize
                value={formik.values.noteText}
                multiline="true"
                minRows={6}
                fullwidth
                style={{ width: '100%' }}
                name="noteText"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          id="selected-records-actions"
          data-test="selected-records-actions"
          className={classes.dialogActions}
        >
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              setIsDialogOpen(false);
              formik.resetForm();
            }}
            disabled={formik.isSubmitting}
          >
            Close
          </Button>
          <Button
            color="primary"
            variant="contained"
            type="submit"
            disabled={!formik.isValid || formik.isSubmitting}
          >
            {formik.isSubmitting && <CircularProgress size={24} style={{ marginRight: '12px' }} />}Create Note
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

CreatePurchaseOrderNoteModal.propTypes = {
  isDialogOpen: PropTypes.bool.isRequired,
  setIsDialogOpen: PropTypes.func.isRequired,
  poNumber: PropTypes.string,
  dispatchId: PropTypes.string,
  onSuccess: PropTypes.func,
};

export default CreatePurchaseOrderNoteModal;
